import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import SearchIcon from "@mui/icons-material/Search";
import {
    Autocomplete, Box, CardHeader, Grid,
    IconButton,
    InputAdornment, Menu,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import axios from "axios";
import * as React from "react";
import ReactCountryFlag from "react-country-flag";
import { NavLink } from "react-router-dom";
import { Avatar } from "../../components/Avatar";
import { CoatOfArms } from "../../components/CoatOfArms";
import { LeagueShield } from "../../components/League";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Config } from "../../config/config";
import { CountryCodes } from "../../utils/countryCodes";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { DashboardUserCreate } from "./createDashboardUser";
import { el } from "date-fns/locale";
import cookie from "react-cookies";

const CIconButton: any = IconButton;

interface DashboardUserStates {
    players: any[];
    loading: boolean;
    filter: PlayerFilterForm;
    form: {
        keyword: undefined | string;
        sort: string;
    }
    totalRecords: number;
    pushModalVisible: boolean;
    selectedPlayer: any | null;
    deviceModalVisible: boolean;
    anchorEl: any;
    rowsPerPage: number;
    page: number;
    menuVisible: boolean;
}

export interface PlayerFilterForm {
    country: string | null;
    platform: string | null;
    status: string | null;
    ban: string | null;
    league: string | null;
    hqLevel: number | null;
    locked: null | boolean;
    in_alliance: null | boolean;
}

export interface SearchFormProps {
    onFormSubmit(): void;
}

interface DashboardUserProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
    form: any;
}

export class DashboardUser extends React.Component<DashboardUserProps, DashboardUser> {
    username: string;

    constructor(props: DashboardUserProps) {
        super(props);

        this.state = {
            filter: {
                country: null,
                platform: null,
                status: null,
                ban: null,
                league: null,
                hqLevel: null,
                in_alliance: null,
                locked: null,
            },
            form: {
                keyword: undefined,
                sort: "joined_DESC",
            },
            players: [],
            loading: true,
            totalRecords: 0,
            pushModalVisible: false,
            selectedPlayer: null,
            deviceModalVisible: false,
            anchorEl: null,
            menuVisible: false,
            // Pagination
            page: 0,
            rowsPerPage: 10,
            roles: [],
        };
        this.username = cookie.load("username");
        
        document.title = Helpers.documentTitle("Players");
    }

    componentDidMount() {
        this.fetchDashboardUsers();
    }

    fetchDashboardUsers = () => {
        const filteredFilters = Object.entries(this.state.filter).reduce((filters, [key, filter]) => {
            if (typeof filter === "object") {
                filters[key] = filter?.code || null;
            } else {
                filters[key] = filter;
            }
            return filters;
        }, {});

        axios.get(`${Config.getInstance.apiUrl}/api/dashboard_users`, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            },
        }).then(response => {
            const data = response.data;
            const {users, roles} = data;
            this.setState({
                loading: false,
                roles,
                players: users || [],
                totalRecords: data.total,
                
            });
        }).catch(_ => {
            PopupMessage.error("Something went wrong!");
        });
    }

    refreshData = () => {
        this.fetchDashboardUsers();
    }

    handleFilter(property: string, value: any) {
        const filterForm = {...this.state.filter};
        filterForm[property] = value;

        this.setState({filter: filterForm});
    }

    removePlayer = (userId: string) => {
        if (confirm("Are you sure you want to delete this user? " + userId)) {   
            axios.post(`${Config.getInstance.apiUrl}/api/dashboard_users/delete`, {
                userId,
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                },
            }).then(result => {
                this.setState((prevState) => ({
                    players: prevState.players.filter(playerData => playerData.id !== userId),
                }));
            }).catch(() => {
                PopupMessage.error("Couldn't delete player");
            });
        }
    };
    

    openRowMenu = (event: any) => this.setState({anchorEl: event.currentTarget}, () => this.setState({menuVisible: true}));
    /**
     * For menu closing we have two functions to make animations smoother
     */
    closeRowMenu = () => this.setState({menuVisible: false});
    clearMenuAnchor = () => this.setState({anchorEl: null});

    getFileActions = () => {
        if (this.state.anchorEl) {
            const buttons: any[] = [];
            const userId = this.state.anchorEl.getAttribute("userid");
            const player = this.state.players.find(pl => pl.id === userId);

            if (player) {
                buttons.push(
                    <MenuItem key="edit-player-key">
                        <DashboardUserCreate type="edit" defaultValues={'test'} refreshData={this.refreshData} {...this.props}/>
                    </MenuItem>
                );
                
                if (this.props.activeEnv !== "prod" && !player.is_online) {
                    buttons.push(
                        <MenuItem key="push-notification-key" style={{color: "#e74c3c"}}
                                  onClick={() => this.removePlayer(player.id, player.deviceid)}>
                            Delete
                        </MenuItem>,
                    );
                }

                return buttons;
            }
        }

        return null;
    };

    renderAllianceInfo(item: any) {
        if (!item.squad) {
            return null;
        }

        return <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CoatOfArms emblem={item.squad.flag} size="small"/>
            <span style={{ marginLeft: 5 }}>{item.squad.name}</span>
        </div>
    }

    formatTableBody = (items: any[]) => {
        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={11}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        const {roles} = this.state;

        return items.map(item => (
            <TableRow
                key={item.id}
                hover={true}
            >
                <TableCell style={{ width: 90 }}>
                    {item.id}
                </TableCell>
                <TableCell style={{width: 145}}>
                    <NavLink to={`/players/${item.id}`}>
                        {item.username}
                    </NavLink>
                </TableCell>
                <TableCell>
                    {item.full_name}
                </TableCell>
                <TableCell align="center" style={{width: 130}}>
                    {item.email}
                </TableCell>
                <TableCell align="center" style={{width: 70}}>
                    {item.permissions}
                </TableCell>
                <TableCell align="center" style={{width: 60}}>
                    {item.page_access}
                </TableCell>
                <TableCell align="center" style={{width: 90}}>
                    {item.otp_enabled.toString()}
                </TableCell>
                <TableCell align="center" style={{width: 90}}>
                    {item.otp_verified.toString()}
                </TableCell>
                <TableCell>
                    <DashboardUserCreate type="edit" roles={roles} defaultValues={item} refreshData={this.refreshData} {...this.props}/>
                </TableCell>
                <TableCell>
                    {this.username !== item.username?  <Button disableElevation variant="contained" color="secondary" onClick={() => this.removePlayer(item.id)}>
                        Delete
                    </Button> : null}
                </TableCell>
            </TableRow>
        ));
    };

    pageContent = (items) => {
        if (!this.state.loading && items.length === 0) {
            return <div className="no-items-placeholder">
                <NotInterestedIcon/>
                <h3>No players found</h3>
            </div>;
        }

        return <React.Fragment>
            <CardHeader
                title="Dashboard Users"
                action={
                    <DashboardUserCreate type="new" roles={this.state.roles} defaultValues={{}} refreshData={this.refreshData} {...this.props}/>
                }
            />
            
            <Table className="min-table-size">
                <TableHead>
                    <TableRow>
                        <TableCell style={{width: 270}} align="center">User id</TableCell>
                        <TableCell style={{width: 152}} align="center">Username</TableCell>
                        <TableCell style={{width: 200}} align="center">Full Name</TableCell>
                        <TableCell style={{width: 120}} align="center">Email</TableCell>
                        <TableCell style={{width: 120}} align="center">Permissions</TableCell>
                        <TableCell style={{width: 100}} align="center">Page Access</TableCell>
                        <TableCell style={{width: 100}} align="center">Otp Enabled</TableCell>
                        <TableCell style={{width: 100}} align="center">Otp Verified</TableCell>
                        <TableCell style={{width: 40}}></TableCell>
                        <TableCell style={{width: 40}}></TableCell>
                        
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.formatTableBody(items)}
                </TableBody>
            </Table>
            <Menu
                id="long-menu"
                anchorEl={this.state.anchorEl}
                open={this.state.menuVisible}
                onClose={this.closeRowMenu}
                TransitionProps={{
                    onExited: this.clearMenuAnchor
                }}
            >
                {this.getFileActions()}
            </Menu>
        </React.Fragment>;
    }

    searchInput(e: any, key: string) {
        const tempForm = {...this.state.form};
        tempForm[key] = e.target.value;

        this.setState({form: tempForm});
    }

    render() {
        /*
        return (
            <div className="no-items-placeholder" style={{ color: 'red' }} >
            <NotInterestedIcon/>
                <h1>You not have access to this page!</h1>
            </div>
        )*/
        return <React.Fragment>
            <div style={{ marginBottom: 20 }}>
                <Paper className="root" elevation={0}>
                    {this.pageContent(this.state.players)}
                </Paper>
            </div>
            </React.Fragment>
        
    }
}
